import { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  RevButton,
  RevDialog,
  RevSpinner,
  RevStepper,
} from "@revmatics/catalyst";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../stores/hooks/hooks.ts";
import { fetchCampaignsData } from "../../../../stores/thunks/campaignsThunk.tsx";
import { fetchPersonasData } from "../../../../stores/thunks/personasThunk.tsx";
import { createCreatorBriefThunk } from "../../../../stores/thunks/creatorBriefsThunk.tsx";
import { StepCampaignSelect } from "./steps/StepCampaignSelect.tsx";
import { StepPersonaSelect } from "./steps/StepPersonaSelect.tsx";
import { StepInterestGroupSelect } from "./steps/StepInterestGroupSelect.tsx";
import { StepCreatorSelect } from "./steps/StepCreatorSelect.tsx";
import {
  CreateBriefFormData,
  CreateBriefDialogProps,
  InterestGroupDisplayData,
} from "./CreateBriefTypes.ts";
import { fetchCreatorsData } from "../../../../stores/thunks/creatorsThunk.tsx";
import { shallowEqual } from "react-redux";
import { clearPersonas } from "../../../../stores/slice/personasSlice.tsx";

const stepperData = [
  { step: 1, title: "Campaign" },
  { step: 2, title: "Persona" },
  { step: 3, title: "Interest Group" },
  { step: 4, title: "Creator" },
];

export const CreateBriefDialog: React.FC<CreateBriefDialogProps> = ({
  isOpen,
  setIsOpen,
  initialCampaignId = null,
  initialPersonaId = null,
  initialCreatorId = null,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const {
    campaigns,
    isLoadingCampaigns,
    personas,
    isLoadingPersonas,
    isLoadingCommunities,
    creators,
    isLoadingCreators,
  } = useAppSelector(
    (state) => ({
      campaigns: state.campaigns.campaigns,
      isLoadingCampaigns: state.campaigns.isLoading,
      personas: state.personas.personas,
      isLoadingPersonas: state.personas.isLoadingList,
      communities: state.discoverCommunities.communities,
      isLoadingCommunities: state.discoverCommunities.isLoadingCommunities,
      selectedPersona: state.personas.selectedPersona,
      creators: state.creators.creators,
      isLoadingCreators: state.creators.isLoading,
    }),
    shallowEqual,
  );
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestedCreatorId, setRequestedCreatorId] = useState<number | null>(
    null,
  );

  const methods = useForm<CreateBriefFormData>({
    mode: "onChange",
    defaultValues: {
      campaignId: initialCampaignId,
      personaId: initialPersonaId,
      interestGroupId: null,
      creatorId: initialCreatorId,
      briefTitle: "",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitting: formIsSubmitting },
    trigger,
    reset,
  } = methods;

  const selectedCampaignId = watch("campaignId");
  const selectedPersonaId = watch("personaId");
  const selectedInterestGroupId = watch("interestGroupId");
  const selectedCreatorId = watch("creatorId");
  const fullSelectedPersona = useMemo(() => {
    if (personas && selectedPersonaId) {
      return personas.find((p) => p.id === selectedPersonaId) || null;
    }
    return null;
  }, [personas, selectedPersonaId]);

  useEffect(() => {
    if (!isOpen) return;

    if (!campaigns && !isLoadingCampaigns) {
      dispatch(fetchCampaignsData(""));
    }

    if (
      initialCreatorId &&
      !isLoadingPersonas &&
      requestedCreatorId !== initialCreatorId
    ) {
      dispatch(
        fetchPersonasData({ creator_id: initialCreatorId, fresh: true }),
      );
      setRequestedCreatorId(initialCreatorId);
      return;
    }

    if (!initialCreatorId && !personas && !isLoadingPersonas) {
      dispatch(fetchPersonasData({}));
    }
  }, [
    isOpen,
    campaigns,
    isLoadingCampaigns,
    personas,
    isLoadingPersonas,
    initialCreatorId,
    requestedCreatorId,
    dispatch,
  ]);

  const campaignOptions = useMemo(() => campaigns || [], [campaigns]);
  const personaOptions = useMemo(() => personas || [], [personas]);

  const interestGroupOptions: InterestGroupDisplayData[] = useMemo(() => {
    if (fullSelectedPersona && fullSelectedPersona.parent_interests) {
      return fullSelectedPersona.parent_interests.map((item) => ({
        id: item.id,
        name: item.taxonomy_type,
        children: item.children,
      }));
    }
    return [];
  }, [fullSelectedPersona]);

  const creatorOptions = useMemo(() => {
    return initialCreatorId
      ? creators?.filter((item) => item.id === initialCreatorId) || []
      : creators || [];
  }, [creators, initialCreatorId]);

  const loadingStepData =
    isLoadingCampaigns || isLoadingPersonas || isLoadingCommunities;

  const handleNextStep = useCallback(async () => {
    let isValid = false;
    let fieldsToValidate: (keyof CreateBriefFormData)[] = [];

    switch (currentStep) {
      case 1:
        fieldsToValidate = ["campaignId"];
        break;
      case 2:
        fieldsToValidate = ["personaId"];
        break;
      case 3:
        fieldsToValidate = ["interestGroupId"];
        break;
      case 4:
        fieldsToValidate = ["creatorId"];
        break;
    }

    isValid = await trigger(fieldsToValidate);

    if (isValid) {
      if (currentStep === 3) {
        const personaId = watch("personaId");
        const interestGroupId = watch("interestGroupId");
        if (personaId && interestGroupId) {
          await dispatch(
            fetchCreatorsData({
              persona_id: personaId,
              interest_id: interestGroupId,
            }),
          );
        }
      }
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, trigger, dispatch, watch]);

  const handleBackStep = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(1);
    setCompletedSteps([]);
    reset();
    setIsSubmitting(false);
  }, [setIsOpen, reset]);

  const onFinalSubmit: SubmitHandler<CreateBriefFormData> = useCallback(
    async (data) => {
      setIsSubmitting(true);

      const payload = {
        campaign: data.campaignId as number,
        persona: data.personaId as number,
        interest_group: data.interestGroupId as string,
        creator: data.creatorId as number,
      };

      dispatch(createCreatorBriefThunk(payload))
        .unwrap()
        .then((newBrief) => {
          handleCloseDialog();
          if (onSuccess) {
            onSuccess(newBrief);
          }
        })
        .finally(() => {
          if (initialCreatorId) {
            dispatch(clearPersonas());
          }
          setIsSubmitting(false);
        });
    },
    [dispatch, handleCloseDialog, initialCreatorId, onSuccess],
  );

  const dialogActions = useMemo(() => {
    const actions: React.ReactElement[] = [];

    if (currentStep > 1) {
      actions.push(
        <RevButton
          key="back"
          text="Back"
          variant="tertiary"
          handleClick={handleBackStep}
        />,
      );
    }

    actions.push(
      <RevButton
        key="cancel"
        text="Cancel"
        variant="secondary"
        handleClick={handleCloseDialog}
      />,
    );

    if (currentStep < stepperData.length) {
      actions.push(
        <RevButton
          key="next"
          text="Next"
          variant="primary"
          handleClick={handleNextStep}
          disabled={!isValid || formIsSubmitting || loadingStepData}
        />,
      );
    } else if (currentStep === stepperData.length) {
      actions.push(
        <RevButton
          key="submit"
          text="Create Brief"
          variant="primary"
          handleClick={handleSubmit(onFinalSubmit)}
          isLoading={isSubmitting || formIsSubmitting || loadingStepData}
          disabled={
            !isValid || isSubmitting || formIsSubmitting || loadingStepData
          }
        />,
      );
    }
    return actions;
  }, [
    currentStep,
    isValid,
    formIsSubmitting,
    handleBackStep,
    handleNextStep,
    handleCloseDialog,
    isSubmitting,
    loadingStepData,
    handleSubmit,
    onFinalSubmit,
  ]);

  const renderStepContent = () => {
    if (loadingStepData) {
      return <RevSpinner size="large" centered />;
    }

    switch (currentStep) {
      case 1:
        return (
          <StepCampaignSelect
            campaigns={campaignOptions}
            selectedCampaignId={selectedCampaignId}
            onSelectCampaign={(id) => setValue("campaignId", id)}
            isLoading={isLoadingCampaigns}
            error={errors.campaignId?.message}
            control={control}
          />
        );
      case 2:
        return (
          <StepPersonaSelect
            personas={personaOptions}
            selectedPersonaId={selectedPersonaId}
            onSelectPersona={(id) => setValue("personaId", id)}
            isLoading={isLoadingPersonas}
            error={errors.personaId?.message}
          />
        );
      case 3:
        return (
          <StepInterestGroupSelect
            interestGroups={interestGroupOptions}
            selectedInterestGroupId={selectedInterestGroupId}
            onSelectInterestGroup={(id) => setValue("interestGroupId", id)}
            isLoading={isLoadingPersonas}
            error={errors.interestGroupId?.message}
          />
        );
      case 4:
        return (
          <StepCreatorSelect
            creators={creatorOptions}
            selectedCreatorId={selectedCreatorId}
            onSelectCreator={(id) => setValue("creatorId", id)}
            isLoading={isLoadingCreators}
            error={errors.creatorId?.message}
          />
        );
      default:
        return null;
    }
  };

  return (
    <RevDialog
      isOpen={isOpen}
      setIsOpen={handleCloseDialog}
      title="Generate Creator Brief"
      btnList={dialogActions}
      scrollableContent={false}
      heightClassName="lg:h-[421px]"
      className="!pt-0"
      mobileSize="xs"
    >
      <div className="flex flex-col h-full w-full">
        <RevStepper
          stepperData={stepperData}
          step={currentStep}
          successSteps={completedSteps}
          stretch
        />
        <div className="pt-6 flex-grow">{renderStepContent()}</div>
      </div>
    </RevDialog>
  );
};
